import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import Countdown from "./Countdown";
import { Gift, ArrowRight } from "lucide-react";
import { sfx } from "@/lib/sounds";

export default function EventCard({ event, index = 0 }) {
  const nav = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      data-testid={`event-card-${event.slug}`}
      onClick={() => { sfx.click(); nav(`/events/${event.slug}`); }}
      className="relative overflow-hidden rounded-3xl bg-[#141414] border-[3px] border-white/10 hover:border-[#FF8C00]/60 hover-blocky blocky-shadow-orange cursor-pointer"
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        {event.image ? (
          <img src={event.image} alt={event.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#FFBF00]/15 via-black to-[#FF8C00]/10 flex items-center justify-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/30">No banner</div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        {event.featured && (
          <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-[#FFBF00] text-black text-[10px] uppercase tracking-widest font-bold" data-testid={`event-featured-${event.slug}`}>★ Featured</div>
        )}
        {event.status && event.status !== "upcoming" && (
          <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-black/70 text-white text-[10px] uppercase tracking-widest">{event.status}</div>
        )}
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]/90">
              {event.category || (event.status === "live" ? "Live Event" : "Upcoming")}
            </div>
            <div className="font-hero text-2xl sm:text-3xl">{event.title}</div>
          </div>
        </div>
      </div>
      <div className="p-5">
        <p className="text-sm text-white/60">{event.description}</p>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Starts In</div>
          <Countdown target={event.starts_at} />
        </div>
        {event.rewards?.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]/80 mb-2">
              <Gift size={12} /> Rewards
            </div>
            <div className="flex flex-wrap gap-1.5">
              {event.rewards.map((r) => (
                <span key={r} className="text-[11px] px-2 py-1 rounded-full border border-white/10 text-white/80">
                  {r}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="text-xs text-white/50">
            <span className="font-mono text-[#FFBF00]">{event.registered || 0}</span> registered
          </div>
          <Link
            to={`/events/${event.slug}`}
            data-testid={`event-open-${event.slug}`}
            onClick={(e) => { e.stopPropagation(); sfx.click(); }}
            className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-[#FFBF00] text-black text-xs font-bold uppercase tracking-wider hover:bg-[#FF8C00] border-2 border-black blocky-shadow-black transition-transform hover:translate-x-1"
          >
            View <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
